import styled from "styled-components/macro";
import { utcToZonedTime, format } from "date-fns-tz";
import { BREAKPOINT_MOBLET } from "../../../constants/constants";
import RavenLogoSVG from "components/Nav/RavenLogo/RavenLogoSVG";
import PrintButton from "../../../components/Reports/Buttons/PrintButton";
import CloseTabButton from "../../../components/Reports/Buttons/CloseTabButton";
import LanguageSelector from "reports/components/LanguageSelector";
import { useTranslation } from "react-i18next";

const dateTimePattern = "yyyy-MM-dd HH:mm";

const BackgroundStyles = styled.div`
  margin-top: 1%;
  position: relative;
  width: 100%;
  min-height: 250px;

  .raven-header-bar {
    position: relative;
    background-color: #133259;
    width: 100%;
    padding: 10pt;
    display: flex;
    justify-content: space-between;
    box-sizing: border-box;
    [class*="RavenLogoSVG"] {
      margin-left: 12px;
    }
  }
  .date-accessed-parent {
    display: flex;
    align-content: baseline;
  }

  .date-accessed {
    color: grey;
    margin-right: 10px;
  }

  .date-time-stamp {
    color: white;
  }

  .header-body {
    position: relative;
    display: flex;
    justify-content: space-between;
    background-color: #00a6d7;
    height: 100%;
  }

  .custom-component-size {
    width: 25%;
  }

  @media print {
    min-height: 0px;
  }
`;

const WeekTimeStyle = styled.div`
  color: white;
  width: 25%;
  text-align: center;
  padding: 10pt;
  justify-content: left;
  margin-bottom: 1%;
  .week-date {
    position: relative;
    .dash {
      position: absolute;
      right: 11px;
      top: -1px;
      bottom: 0;
    }
  }
  .week-subtitle {
  }

  .week-count {
    line-height: 1;
    justify-content: center;
    font-size: 64px;
  }

  @media (max-width: ${BREAKPOINT_MOBLET}px) {
    .week-count {
      font-size: 48px;
    }

    .week-date {
      font-size: 10pt;
    }
    padding: 5px;
  }
`;

const CenterPieceStyle = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
  padding: 10pt;

  p {
    color: white;
    text-align: center;
  }

  .center-piece-holder {
    height: 100%;
    position: relative;
    display: flex;
    justify-content: center;
    box-sizing: border-box;
    width: fit-content;
  }

  .customer-name {
    color: white;
    font-weight: bold;
    text-align: center;
  }

  .report-title {
    font-weight: bold;
    text-align: center;
    font-size: 28px;
    color: white;
    overflow: hidden;
    white-space: nowrap;
    margin-bottom: 10pt;
  }

  .header-image {
    top: 25%;
    right: 40pt;
    position: relative;
  }

  .image1 {
    height: 40px;
  }

  @media (max-width: ${BREAKPOINT_MOBLET}px) {
    padding: 10pt 0;

    .report-title {
      font-size: 22px;
    }

    .header-image {
      right: 12pt;
    }

    .image1 {
      width: auto;
      height: 30px;
    }
  }
`;

const CenterPiece = ({ processName, custName, custLocation }) => {
  const { t } = useTranslation("WeeklyAggregate");
  return (
    <CenterPieceStyle>
      <div className="center-piece-holder">
        <div className="header-image">
          <img className="image1" src="/images/speedo-white.png" alt="" />
        </div>
        <div>
          <div className="report-title">{t("weeklyAggregate")}</div>
          <p>{processName}</p>
          <div className="customer-name">{custName}</div>
          <p>{custLocation}</p>
        </div>
      </div>
    </CenterPieceStyle>
  );
};

const WeekAndTime = ({ weekNumber, ge, lt, timeZone }) => {
  const formatDateTime = t =>
    format(
      utcToZonedTime(new Date(t).toISOString(), timeZone),
      dateTimePattern,
      { timeZone }
    );

  const { t } = useTranslation("WeeklyAggregate");
  return (
    <WeekTimeStyle>
      <div className="week-subtitle">{t("week")}</div>
      <div className="week-count">{weekNumber}</div>
      <div className="week-date">
        {formatDateTime(ge)}
        <div className="dash"> - </div>
      </div>
      <div className="week-date"> {formatDateTime(lt)}</div>
    </WeekTimeStyle>
  );
};

const RavenHeaderStyles = styled.div`
  .rightSide {
    display: flex;
    justify-content: center;
    align-items: center;
    p {
      padding-right: 1em;
    }

    .MuiIconButton-root {
      color: white;

      @media print {
        display: none;
      }
    }
  }
`;

export const RavenHeader = ({
  timeZone,
  dataRetrievedAt,
  shouldShowCloseTab,
  shouldShowTranslate,
}) => {
  const formatDateTime = t =>
    format(
      utcToZonedTime(new Date(t).toISOString(), timeZone),
      dateTimePattern,
      { timeZone }
    );
  const { t } = useTranslation("WeeklyAggregate");
  return (
    <RavenHeaderStyles>
      <div className="raven-header-bar">
        <RavenLogoSVG />
        <div className="rightSide">
          <div className="date-accessed-parent">
            <div className="date-accessed">{t("dateRetrieved")}</div>
            <div className="date-time-stamp">
              {formatDateTime(dataRetrievedAt)}
            </div>
          </div>
          <PrintButton />
          {shouldShowTranslate && <LanguageSelector />}
          {shouldShowCloseTab && <CloseTabButton />}
        </div>
      </div>
    </RavenHeaderStyles>
  );
};

export default function WeeklyHeader({
  weekNumber,
  ge,
  lt,
  timeZone,
  oeeTarget,
  dataRetrievedAt,
  processName,
  custName,
  custLocation,
  customComponent,
  shouldShowCloseTab,
  shouldShowTranslate = false,
}) {
  return (
    <BackgroundStyles>
      <RavenHeader
        dataRetrievedAt={dataRetrievedAt}
        timeZone={timeZone}
        shouldShowCloseTab={shouldShowCloseTab}
        shouldShowTranslate={shouldShowTranslate}
      />
      <div className="header-body">
        <WeekAndTime {...{ weekNumber, ge: ge, lt: lt, timeZone: timeZone }} />
        <CenterPiece
          custName={custName}
          processName={processName}
          custLocation={custLocation}
        />
        <div className="custom-component-size">{customComponent}</div>
      </div>
    </BackgroundStyles>
  );
}
